import { useFormaliteService } from '@europrocurement/l2d-domain';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import GreffeCard, { GreffeChoice } from './GreffeCard';

export type SelectGreffeContentProps = {
    onSelectGreffe: (greffes: Array<GreffeChoice>) => void;
    currentChoices: Array<GreffeChoice>;
};

const SelectGreffeContent: React.FunctionComponent<SelectGreffeContentProps> = function (props) {
    const { currentChoices, onSelectGreffe } = props;
    const [choices, setChoices] = useState<Array<GreffeChoice>>([]);
    const [query, setQuery] = useState<string>('');
    const [searching, setSearching] = useState<boolean>(false);
    const { greffeApi } = useFormaliteService();

    const refreshChoices = useDebouncedCallback(() => {
        setSearching(true);
        greffeApi
            .apiGreffesGetCollection({ greffes: query, itemsPerPage: 200, orderGreffes: 'asc' })
            .then((response) => {
                setChoices(
                    (response.data['hydra:member'] || []).map((item) => ({
                        id: item.id as number,
                        greffes: item.greffes as string,
                    })),
                );
            })
            .catch(() => setChoices([]))
            .finally(() => setSearching(false));
    }, 500);

    useEffect(() => refreshChoices(), [query, refreshChoices]);

    const hasResults = searching || !query.length || choices.length > 0;
    const selectedIds = currentChoices.map((item) => item.id);

    return (
        <Box sx={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '1em 0' }}>
            <Typography variant="l2dh6">Rechercher un greffe</Typography>
            <Box sx={{ marginTop: '1.5em', position: 'relative' }}>
                <TextField
                    value={query}
                    fullWidth
                    placeholder="Rechercher un greffe"
                    onChange={(e) => {
                        setQuery(e.target.value);
                    }}
                />
                {searching && (
                    <CircularProgress
                        sx={{ position: 'absolute', right: '20px', top: 'calc(50% - 10px)' }}
                        size="16px"
                    />
                )}
            </Box>
            {!hasResults && (
                <Box
                    sx={{
                        marginTop: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1em 0',
                    }}
                >
                    Votre recherche n&lsquo;a donné aucun résultat
                </Box>
            )}
            {hasResults && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em 0' }}>
                    {choices.map((choice) => (
                        <GreffeCard
                            key={choice.id}
                            id={choice.id}
                            greffes={choice.greffes}
                            checked={selectedIds.includes(choice.id)}
                            onClick={(greffe) => {
                                if (selectedIds.includes(greffe.id)) {
                                    onSelectGreffe(
                                        currentChoices.filter((c) => c.id !== greffe.id),
                                    );
                                } else {
                                    onSelectGreffe([...currentChoices, greffe]);
                                }
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default SelectGreffeContent;
